import { UsageLimitEntitlementName } from '@jupiterone/entitlement-types';
import { IconButton, Menu, MenuItem, Tooltip } from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/styles';
import CreateRuleModal from 'jupiter-web-alerts/src/components/Rules/CreateRuleModal/CreateRuleModal';
import React, { useState } from 'react';
import { useAnchorElState, useBooleanState } from 'react-use-object-state';
import { useABAC, VariantQuestionModal } from '../..';
import { DeferredResponseFormat } from '../../../types/J1QL';
import { EntitlementsUsageTooltip } from '../../entitlements';
import { JupiterTheme } from '../../theme/types';
import useFormatQueryUrl from '../../util/useFormatQueryUrl';
import AddToInsightsMenu from '../QuestionResult/AddToInsightsMenu';
import QueryExportMenu from './QueryExportMenu';
import ShareModal from './ShareModal';
import { ResultFormat } from './types';
import toolbarItemDescriptors, {
  ToolbarItemDescriptor,
} from './util/toolbarItemDescriptors';
import { RulesListProvider } from '../../context/RulesListContext';

const useStyles = makeStyles((theme: JupiterTheme) =>
  createStyles({
    iconButton: {
      borderRadius: 0,
      color: theme.palette.grey[500],
      minHeight: theme.spacing(3.125),
      padding: 0,
      alignSelf: 'flex-start',
      '&:hover': {
        color: theme.palette.primary.main,
      },
      '&:active': {
        color: theme.palette.primary.main,
      },
      '&:focus': {
        color: theme.palette.grey[500],
      },
    },
    tooltip: {
      margin: theme.spacing(0, 0, 2.25, 1.5),
    },
    viewButtonItem: {
      fontWeight: 'normal',
      lineHeight: 1,
      marginRight: theme.spacing(2.25),
    },
    viewButtonItemSelected: {
      color: theme.palette.primary.main,
      backgroundColor: theme.palette.coolGrey[100],
      opacity: 1,
    },
  })
);

interface Props {
  savable?: boolean;
  onRemoveResult?: VoidFunction;
  selectedFormat?: ResultFormat;
  onSelectedFormat?: (format: ResultFormat) => void;
  onSelectedDownload?: (format: DeferredResponseFormat) => void;
  isExporting?: boolean;
  exportable?: boolean;
  query: string;
  toolbarIcons?: ToolbarItemDescriptor[];
  addToInsights?: boolean;
  expandable?: boolean;
  expanded?: boolean;
  hideAlert?: boolean;
  hasResults?: boolean;
  onToggleExpanded?: VoidFunction;
}

const QueryToolbarActions = ({
  savable,
  onRemoveResult,
  selectedFormat,
  onSelectedFormat,
  onSelectedDownload,
  isExporting,
  exportable,
  query,
  toolbarIcons = [],
  addToInsights,
  expandable,
  expanded,
  hideAlert,
  hasResults = true,
  onToggleExpanded,
}: Props) => {
  const classes = useStyles();
  const { isAdmin, enforce } = useABAC();
  const { formatQueryUrl } = useFormatQueryUrl();
  const shareModalOpen = useBooleanState(false);
  const saveQuestionModalOpen = useBooleanState(false);
  const createRuleModalOpen = useBooleanState(false);
  const [queryUrl, setQueryUrl] = useState<string>('');

  const viewAsMenu = useAnchorElState(null);
  const exportMenu = useAnchorElState(null);
  const addToInsightsModal = useAnchorElState(null);

  const handleFormatMenuSelect = (format: ResultFormat) => () => {
    viewAsMenu.clearAnchorEl();
    onSelectedFormat && onSelectedFormat(format);
  };

  const openShareDialog = async () => {
    await formatQueryUrl({ query })
      .then(setQueryUrl)
      .then(shareModalOpen.setTrue);
  };

  const toolbarElements = [
    ...toolbarIcons,
    ...toolbarItemDescriptors({
      query,
      isExporting,
      viewAs: {
        enabled: hasResults && Boolean(onSelectedFormat),
        onClick: viewAsMenu.setAnchorEl,
      },
      exportItem: {
        enabled: exportable,
        onClick: exportMenu.setAnchorEl,
      },
      alert: {
        enabled: !hideAlert,
        onClick: createRuleModalOpen.setTrue,
      },
      star: {
        entitlement: UsageLimitEntitlementName.CUSTOM_QUESTIONS,
        enabled: savable,
        onClick: saveQuestionModalOpen.setTrue,
      },
      remove: {
        enabled: Boolean(onRemoveResult),
        onClick: onRemoveResult,
      },
      expand: {
        enabled: expandable && !expanded,
        onClick: onToggleExpanded,
      },
      minimize: {
        enabled: expanded,
        onClick: onToggleExpanded,
      },
      addToInsights: {
        enabled: isAdmin && addToInsights,
        onClick: addToInsightsModal.setAnchorEl,
      },
    }),
  ];

  const menuItemClasses = {
    root: classes.viewButtonItem,
    selected: classes.viewButtonItemSelected,
  };
  return (
    <>
      {toolbarElements.map(({ title, onClick, icon, entitlement, abac }) => {
        return !abac || enforce(abac) ? (
          <div className={classes.tooltip} key={title}>
            {entitlement ? (
              <EntitlementsUsageTooltip
                entitlement={entitlement}
                key={title}
                fallbackTitle={title}
                enterDelay={500}
              >
                {(hasEntitlement) => (
                  <IconButton
                    aria-label={title}
                    aria-haspopup="true"
                    className={classes.iconButton}
                    onClick={onClick}
                    disabled={!hasEntitlement}
                  >
                    {icon}
                  </IconButton>
                )}
              </EntitlementsUsageTooltip>
            ) : (
              <Tooltip key={title} title={title} enterDelay={500}>
                <IconButton
                  aria-label={title}
                  aria-haspopup="true"
                  className={classes.iconButton}
                  onClick={onClick}
                >
                  {icon}
                </IconButton>
              </Tooltip>
            )}
          </div>
        ) : null;
      })}
      <QueryExportMenu
        anchor={exportMenu}
        menuItemClasses={menuItemClasses}
        onClickShareLink={openShareDialog}
        onClickDownloadAsCSV={() =>
          onSelectedDownload?.(DeferredResponseFormat.CSV)
        }
        onClickDownloadAsJSON={() =>
          onSelectedDownload?.(DeferredResponseFormat.JSON)
        }
      />

      <Menu
        anchorEl={viewAsMenu.anchorEl}
        open={Boolean(viewAsMenu.anchorEl)}
        onClose={viewAsMenu.clearAnchorEl}
      >
        {Object.keys(ResultFormat).map((resultFormat) => (
          <MenuItem
            key={(ResultFormat as any)[resultFormat]}
            disabled={resultFormat === selectedFormat}
            selected={resultFormat === selectedFormat}
            onClick={handleFormatMenuSelect(
              (ResultFormat as any)[resultFormat]
            )}
            classes={menuItemClasses}
          >
            {resultFormat}
          </MenuItem>
        ))}
      </Menu>
      <ShareModal
        open={shareModalOpen.state}
        link={queryUrl}
        type="Query"
        onClose={shareModalOpen.setFalse}
      />
      <VariantQuestionModal
        open={saveQuestionModalOpen.state}
        variant="save"
        question={{
          id: '',
          name: '',
          description: '',
          title: '',
          lastUpdatedTimestamp: 0,
          queries: [{ query, name: '' }],
          compliance: [],
        }}
        onClose={saveQuestionModalOpen.setFalse}
      />
      {enforce(['adminRules']) && (
        <RulesListProvider>
          <CreateRuleModal
            open={createRuleModalOpen.state}
            onClose={createRuleModalOpen.setFalse}
            query={query}
          />
        </RulesListProvider>
      )}
      {enforce(['adminInsights']) && (
        <AddToInsightsMenu
          open={Boolean(addToInsightsModal.anchorEl)}
          anchorEl={addToInsightsModal.anchorEl}
          onClose={addToInsightsModal.clearAnchorEl}
          query={query}
        />
      )}
    </>
  );
};

export default QueryToolbarActions;
